import type { PanelItem, SliderElementValue } from '@vedalib/editor/lib/elements'
import { ProgressType } from '@vedalib/editor/lib/elements'
import { useMemo } from 'react'
import React from 'react'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { FieldArray, FieldSegmented, FieldSelect, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { Typography } from 'components/uiKit/Typography'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { chainName } from 'utils/form'

import { useThroughElementItemsArrayEffect } from '../../utils/formEffects'
import SliderFormValueItem from './SliderFormValueItem'
import type { SliderFormValueType } from './sliderTypes'

const useShowProgress = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<SliderElementValue>(name, 1)
  return useMemo(() => ({ hidden: !value.hasProgress }), [value.hasProgress])
}

const useShowProgressDeep = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<SliderElementValue>(name, 2)
  return useMemo(() => ({ hidden: !value.hasProgress }), [value.hasProgress])
}

const useShowNumber = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<SliderElementValue>(name, 2)
  return useMemo(() => ({ hidden: value.type !== ProgressType.number }), [value.type])
}

const SLIDES = genElementValueField({
  as: FieldArray<PanelItem>,
  name: 'items',
  props: {
    RenderItem: SliderFormValueItem,
    min: 1,
    max: 184,
  },
  useConfigs: [useThroughElementItemsArrayEffect],
})

const HAS_LOOP = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'hasLoop',
  label: t('elements.slider.form.hasLoop'),
  defaultValue: true,
})

const HAS_PROGRESS = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'hasProgress',
  label: t('input.label.progress'),
  defaultValue: true,
})

const TYPE = genElementValueField({
  name: 'type',
  as: FieldSelect<ProgressType>,
  label: t('input.label.type'),
  defaultValue: ProgressType.dot,
  props: {
    fluid: true,
    options: [
      { value: ProgressType.dot, label: t('input.option.points') },
      { value: ProgressType.number, label: t('input.option.number') },
    ],
  },
  useConfigs: [useShowProgress],
})

const HAS_FIRST = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'includeSlide.first',
  label: t('elements.slider.form.first'),
  defaultValue: true,
  useConfigs: [useShowProgressDeep, useShowNumber],
})

const HAS_LAST = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'includeSlide.last',
  label: t('elements.slider.form.last'),
  defaultValue: true,
  useConfigs: [useShowProgressDeep, useShowNumber],
})

const SliderFormValue: SliderFormValueType = ({ name, element }) => {
  return (
    <Padding bottom gap sides>
      <Typography styleType='hint'>{t('elements.slider.form.loopHint')}</Typography>
      <FormField config={chainName(name, HAS_LOOP)} />
      <FormField config={chainName(name, HAS_PROGRESS)} />
      <FormField config={chainName(name, TYPE)} />
      {element.value.type === ProgressType.number && element.value.hasProgress && (
        <Typography styleType='hint'>{t('elements.slider.form.includeSlidesHint')}</Typography>
      )}
      <FormField config={chainName(name, HAS_FIRST)} />
      <FormField config={chainName(name, HAS_LAST)} />
      <FormField config={chainName(name, SLIDES)} />
    </Padding>
  )
}

export default React.memo(SliderFormValue)
